<template>
  <div class="analysis-configurator">
    <Transition name="slide-fade" appear>
      <div v-if="isOpen" class="mb-0 overflow-hidden bg-white shadow">
        <div class="flex items-center justify-between bg-gray-50 px-4 py-3">
          <div class="flex items-center space-x-3">
            <button
              type="button"
              @click="$emit('back')"
              class="p-1 text-gray-500 transition-colors hover:text-gray-700"
              :title="t('registers.analysis.correlation.back')">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </button>
            <h3 class="text-lg font-semibold text-gray-800">
              {{ t('registers.analysis.correlation.title') }}
            </h3>
          </div>
        </div>

        <div class="px-6 py-4">
          <Form
            :key="correlationFormKey"
            v-slot="{ errors, setFieldValue }"
            :validation-schema="correlationAnalysisSchema"
            class="space-y-4">
            <div>
              <label class="mb-2 block text-sm font-medium text-gray-700">
                {{ t('registers.analysis.correlation.secondary_sensor') }}
              </label>
              <div class="flex items-center gap-2">
                <div class="flex-1">
                  <DropdownSensors
                    name="secondarySensorId"
                    :options="correlationSensorOptions"
                    :already-selected-option="selectedSecondarySensor || undefined"
                    :constrain-to-parent="true"
                    @option-selected="(sensor: ISensor) => onSecondarySensorSelected(sensor, setFieldValue)" />
                </div>
                <button
                  type="button"
                  :disabled="addDisabled"
                  :class="addDisabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:bg-blue-50'"
                  class="rounded-none border border-transparent px-3 py-2 text-sm font-medium text-blue"
                  :aria-label="t('registers.analysis.correlation.add_sensor')"
                  :title="addDisabled
                    ? t('registers.analysis.correlation.add_sensor_duplicate')
                    : t('registers.analysis.correlation.add_sensor_hint')"
                  @click.prevent="setFieldValue('secondarySensorId', ''); addSelectedSensor(setFieldValue)">
                  {{ t('registers.analysis.correlation.add_sensor') }}
                </button>
              </div>
              <p v-if="errors.secondarySensorId" class="mt-1 text-sm text-red">
                {{ errors.secondarySensorId }}
              </p>
            </div>

            <div>
              <div class="grid grid-cols-[repeat(5,max-content)] gap-2">
                <TransitionGroup name="sensor-chip">
                  <span
                    v-for="entry in addedCorrelationSensors"
                    :key="entry.sensorId"
                    :class="[
                      'inline-flex items-center gap-1 whitespace-nowrap px-3 py-2 text-sm font-medium text-white opacity-90 hover:opacity-100',
                      ...sensorBadgeClass(entry),
                    ]">
                    {{ sensorNameOf(entry.sensorId) }}
                    <button
                      type="button"
                      class="ml-1 shrink-0 text-white/75 transition-colors hover:text-white"
                      :aria-label="t('registers.analysis.correlation.remove_sensor')"
                      :title="t('registers.analysis.correlation.remove_sensor')"
                      @click.prevent="onRemoveSensor(entry.sensorId)">
                      <XMarkIcon class="h-3.5 w-3.5" />
                    </button>
                  </span>
                </TransitionGroup>
              </div>
              <p v-if="!addedCorrelationSensors.length" class="text-sm text-gray-400">
                {{ t('registers.analysis.correlation.sensors_empty') }}
              </p>
            </div>

            <div v-if="isLoading" class="flex items-center justify-center py-1">
              <IcLoader
                class="h-5 w-5 animate-spin"
                role="status"
                :aria-label="t('registers.analysis.correlation.loading')" />
            </div>
            <p v-if="correlationError" class="text-sm text-red">
              {{ t('registers.analysis.correlation.fetch_error') }}
            </p>
          </Form>
        </div>

        <div class="flex items-center justify-end bg-gray-2 p-4">
          <button
            type="button"
            @click="$emit('back')"
            class="bg-gray-200 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500">
            {{ t('registers.analysis.correlation.back') }}
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import DropdownSensors from '@/components/Globals/Inputs/DropdownSensors.vue'
import IcLoader from '@/components/Icons/IcLoader.vue'
import { useI18n } from '@/composable/useI18n'
import { getSensorColor } from '@/interfaces/enums'
import type { ISensor } from '@/interfaces/entities/sensor'
import yup from '@/plugins/yup'
import type { IRegisterChartSensorData } from '@/utils/components/register-chart'
import { useRegisterStore } from '@/store/register'
import { filterCorrelationSensors } from '@/utils/dashboard/time-series-correlation'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { Form } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import { TransitionGroup } from 'vue'

const correlationAnalysisSchema = yup.object({
  secondarySensorId: yup.string().optional(),
})

interface Props {
  isOpen: boolean
  sensorData?: { sensor?: ISensor } | null
  sensorIndex?: number | null
}

interface Emits {
  (e: 'close'): void
  (e: 'back'): void
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  sensorData: null,
  sensorIndex: null,
})

defineEmits<Emits>()

const { t } = useI18n()
const registerStore = useRegisterStore()
const { listSensors } = storeToRefs(registerStore)

const selectedSecondarySensor = ref<ISensor | null>(null)
const correlationFormKey = ref(0)

const primarySensor = computed(() => props.sensorData?.sensor ?? null)

const addedCorrelationSensors = computed<Array<{ sensorId: number; data: IRegisterChartSensorData | null }>>(() => {
  const sensorIndex = props.sensorIndex
  if (sensorIndex === null || sensorIndex === undefined) return []
  return registerStore.getCorrelationSensors(sensorIndex)
})

const addedSensorIds = computed(() => addedCorrelationSensors.value.map((entry) => entry.sensorId))

const correlationSensorOptions = computed(() =>
  filterCorrelationSensors(listSensors.value, primarySensor.value, addedSensorIds.value),
)

const addDisabled = computed(() => {
  const candidate = selectedSecondarySensor.value
  if (!candidate) return true
  return addedSensorIds.value.includes(candidate.id)
})

const isLoading = computed(() => {
  if (props.sensorIndex === null || props.sensorIndex === undefined) return false
  return registerStore.isLoadingCorrelation(props.sensorIndex)
})

const correlationError = computed(() => {
  if (props.sensorIndex === null || props.sensorIndex === undefined) return null
  return registerStore.getCorrelationError(props.sensorIndex)
})

function sensorNameOf(sensorId: number): string {
  const entry = addedCorrelationSensors.value.find((item) => item.sensorId === sensorId)
  if (entry?.data?.sensor?.name) return entry.data.sensor.name
  const sensor = listSensors.value.find((item) => String(item.id) === String(sensorId))
  return sensor?.name ?? String(sensorId)
}

function sensorOf(entry: { sensorId: number }): ISensor | undefined {
  return listSensors.value.find((item) => String(item.id) === String(entry.sensorId))
}

/** Badge no padrão dos cards de grupo: cor por tipo de sensor + pulso de alarme aberto. */
function sensorBadgeClass(entry: { sensorId: number }): Array<string | false> {
  const sensor = sensorOf(entry)
  if (!sensor?.sensor_type) return ['bg-slate-400']
  return [getSensorColor(sensor.sensor_type), sensor.has_open_alarm && 'sensor-with-open-alarm']
}

async function ensureSensorsLoaded() {
  if (!listSensors.value.length) {
    await registerStore.fetchSensors()
  }
}

async function onSecondarySensorSelected(
  sensor: ISensor,
  setFieldValue: (field: string, value: unknown) => void,
) {
  selectedSecondarySensor.value = sensor
  setFieldValue('secondarySensorId', String(sensor.id))
}

function addSelectedSensor(setFieldValue: (field: string, value: unknown) => void) {
  const sensor = selectedSecondarySensor.value
  const sensorIndex = props.sensorIndex
  if (!sensor || sensorIndex === null || sensorIndex === undefined) return
  if (addDisabled.value) return

  // Sem await: o push da badge é síncrono na store; o fetch de readings roda em
  // background e atualiza `entry.data` quando a API responder. A limpeza da UI
  // acontece imediatamente, sem "travada" até a renderização.
  registerStore.addCorrelationSensor(sensorIndex, sensor)
  selectedSecondarySensor.value = null
  setFieldValue('secondarySensorId', '')
  correlationFormKey.value += 1
}

function onRemoveSensor(sensorId: number) {
  const sensorIndex = props.sensorIndex
  if (sensorIndex === null || sensorIndex === undefined) return
  registerStore.removeCorrelationSensor(sensorIndex, sensorId)
}

async function hydrateSelectedSecondarySensor() {
  await ensureSensorsLoaded()
  selectedSecondarySensor.value = null
  correlationFormKey.value += 1
}

watch(
  () => props.isOpen,
  (open) => {
    if (!open) return
    hydrateSelectedSecondarySensor()
  },
  { immediate: true },
)
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

.sensor-chip-enter-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.sensor-chip-enter-from {
  opacity: 0;
  transform: scale(0.85) translateY(4px);
}

.sensor-chip-enter-to {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.sensor-chip-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.sensor-chip-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.sensor-chip-move {
  transition: transform 0.25s ease;
}
</style>